import { formatListName } from "@/shared/utils/formatListName";
import "./legacy-headlines.css";

import LegacyNewsquawkHeadlineItem from "./LegacyNewsquawkHeadlineItem";
import LegacyLegend from "./LegacyLegend";
import { useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import { CommonWidgetProps } from "../../types";
import { buildHighlightCandidates } from "@/shared/utils/highlightMatches";
import { Headline } from "../models/Headline";
import DeleteConfirmationDialog from "../DeleteConfirmationDialog";
import EditHeadlineFormDialog from "../EditHeadlineFormDialog";
import PollingModeIndicator from "@/shared/components/PollingModeIndicator";
import EmptyPlaceholder from "@/ui-components/empty-placeholder";
import { usePortfoliosCRUD } from "@/pages/PortfolioBuilder/api/usePortfoliosCRUD";
import { usePortfolioBuilderOptional } from "@/pages/PortfolioBuilder/components/PortfolioBuilderContext";
import { applyFilters } from "../hooks/useHeadlinesHighlightFilterMenu";
import { useHeadlinesFilterPanel } from "../hooks/useHeadlinesFilterPanel";
import { useHeadlinesHighlightPanel } from "../hooks/useHeadlinesHighlightPanel";
import FilterHighlightPanels from "@/ui-components/FilterPanel/FilterHighlightPanels";
import useStableArray from "@/hooks/useStableArray";
import { useHeadlineQueriesCRUD } from "../api/headline-query/useHeadlineQueriesCRUD";
import { CategoryQuery } from "../models/CategoryQuery";
import { mapPortfolioIdsToPortfolios } from "../../firehose-widgets/shared/hooks/searchUtils";
import {
  useHeadlines,
  useHeadlinesCRUD,
  useSearchHeadlines,
  useIsPollingMode,
} from "@/shared/api/newsquawk-headlines";
import { useHeadlinesFilterStore } from "@/shared/api/newsquawk-headlines/HeadlinesFilterStore";
import { trackFilterApplied } from "@/utils/gtm";
import { menuCategories } from "../models/categories";
import { VirtuosoWrapper, GroupingUtils } from "@/ui-components/grouped-virtuoso";

/** Resolve tag IDs to category names using the static menuCategories list */
const allCategoryItems = menuCategories.flatMap((group) => group.items);
function resolveTagNames(tagIds: (string | number)[]): string {
  return tagIds
    .map((id) => allCategoryItems.find((item) => item.id === Number(id))?.title)
    .filter(Boolean)
    .join(", ");
}

/**
 * Legacy-skinned Newsquawk Headlines widget used full-screen at /headlines.
 *
 * Reuses the live v2 data/filter/search orchestration verbatim; only the
 * presentation is re-skinned to the old portal look. The delayed/demo data
 * path is intentionally absent — /headlines is authenticated & live-only.
 */
function LegacyNewsquawkHeadlines(props: CommonWidgetProps) {
  const isPollingMode = useIsPollingMode();

  const {
    isLoading: headlinesLoading,
    headlines,
    loadNextPage,
    hasMore: hasMoreHeadlines,
  } = useHeadlines();

  const { deleteMutation } = useHeadlinesCRUD();

  const {
    searchHeadlines,
    searchResults,
    clearSearchResults,
    isLoading: searchLoading,
    loadNext: loadNextSearchPage,
    hasMore: hasMoreSearchResults,
    isLoadingMore: isLoadingMoreSearchResults,
  } = useSearchHeadlines();

  const [editingItem, setEditingItem] = useState<Headline | null>(null);
  const [deletingItem, setDeletingItem] = useState<Headline | null>(null);

  const { portfolios } = usePortfoliosCRUD();
  const { queriesQuery: categoryQueries } = useHeadlineQueriesCRUD();
  const portfolioBuilderContext = usePortfolioBuilderOptional();

  const [lastOpenedPanel, setLastOpenedPanel] = useState<
    "filter" | "highlight" | null
  >(null);

  const handleHighlightPanelOpen = useCallback(() => {
    setLastOpenedPanel("highlight");
  }, []);

  const handleFilterPanelOpen = useCallback(() => {
    setLastOpenedPanel("filter");
  }, []);

  // Highlight panel
  const {
    selections: highlightSelections,
    setSelections: setHighlightSelections,
    isPanelOpen: isHighlightPanelOpen,
    closePanel: closeHighlightPanel,
    filterMenus: highlightMenus,
    clearSelections: clearHighlightSelections,
    order: highlightOrder,
    moveInOrder: moveHighlight,
  } = useHeadlinesHighlightPanel({
    setActions: props.headerOptions?.setActions,
    pushAction: props.headerOptions?.pushAction,
    persistKey: props.widgetCompositeId,
    onOpen: handleHighlightPanelOpen,
    closeSearch: props.headerOptions?.closeSearch,
    onSearchOpen: props.headerOptions?.onSearchOpen,
  });

  // Filter panel
  const {
    selections: filterSelections,
    setSelections: setFilterSelections,
    isPanelOpen: isFilterPanelOpen,
    closePanel: closeFilterPanel,
    filterMenus,
    clearSelections: clearFilterSelections,
    filterMode,
    tagWatchlists,
  } = useHeadlinesFilterPanel({
    setActions: props.headerOptions?.setActions,
    pushAction: props.headerOptions?.pushAction,
    persistKey: props.widgetCompositeId,
    onOpen: handleFilterPanelOpen,
    closeSearch: props.headerOptions?.closeSearch,
    onSearchOpen: props.headerOptions?.onSearchOpen,
  });

  // Panel coordination: when one opens, close the other
  useEffect(() => {
    if (lastOpenedPanel === "filter" && isHighlightPanelOpen) {
      closeHighlightPanel();
    } else if (lastOpenedPanel === "highlight" && isFilterPanelOpen) {
      closeFilterPanel();
    }
  }, [
    lastOpenedPanel,
    isFilterPanelOpen,
    isHighlightPanelOpen,
    closeFilterPanel,
    closeHighlightPanel,
  ]);

  // Sync preview state from PortfolioBuilder to filter selections
  useEffect(() => {
    if (!portfolioBuilderContext) return;

    const { previewState } = portfolioBuilderContext;

    if (previewState.type === "watchlist" && previewState.id !== null) {
      setFilterSelections((prev) => ({
        ...prev,
        "ticker-watchlists-filter": [String(previewState.id)],
        "keyword-watchlists-filter": [],
        "category-filter": [],
        "basic-filter": [],
        "tag-watchlists-filter": [],
      }));
    } else if (previewState.type === "filter" && previewState.id !== null) {
      setFilterSelections((prev) => ({
        ...prev,
        "category-filter": [String(previewState.id)],
        "ticker-watchlists-filter": [],
        "keyword-watchlists-filter": [],
        "basic-filter": [],
        "tag-watchlists-filter": [],
      }));
    } else if (previewState.type === "tagWatchlist" && previewState.tagIds) {
      const tagIds = previewState.tagIds;
      setFilterSelections((prev) => ({
        ...prev,
        "basic-filter": tagIds,
        "ticker-watchlists-filter": [],
        "keyword-watchlists-filter": [],
        "category-filter": [],
        "tag-watchlists-filter": [],
      }));
    } else {
      setFilterSelections((prev) => ({
        ...prev,
        "ticker-watchlists-filter": [],
        "keyword-watchlists-filter": [],
        "category-filter": [],
        "basic-filter": [],
        "tag-watchlists-filter": [],
      }));
    }
    // portfolioBuilderContext is intentionally read via .previewState only
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [portfolioBuilderContext?.previewState, setFilterSelections]);

  // Global filter store actions for syncing portfolio selections (sound alerts)
  // Identifies this widget instance in the shared alert store, so sibling
  // Headlines widgets don't overwrite (or on unmount erase) each other's
  // selections. Popouts/standalone renders have no composite id.
  const fallbackInstanceId = useId();
  const instanceKey = props.widgetCompositeId ?? fallbackInstanceId;

  const setPortfoliosFilter = useHeadlinesFilterStore(
    (state) => state.setPortfoliosFilter
  );
  const setPortfoliosHighlight = useHeadlinesFilterStore(
    (state) => state.setPortfoliosHighlight
  );

  const basicFilterSelected = useStableArray(
    filterSelections["basic-filter"] || []
  );

  const tagWatchlistsFilterSelected = useStableArray(
    filterSelections["tag-watchlists-filter"] || []
  );

  const tagWatchlistTagIds = useMemo(() => {
    if (tagWatchlistsFilterSelected.length === 0) return [];
    return tagWatchlistsFilterSelected.flatMap((watchlistId) => {
      const watchlist = tagWatchlists.find((w) => String(w.id) === watchlistId);
      return watchlist?.tags || [];
    });
  }, [tagWatchlistsFilterSelected, tagWatchlists]);

  const queriesToSearch = useStableArray(
    filterSelections["category-filter"]
      ?.map((id) => {
        if (categoryQueries.data) {
          return categoryQueries.data.find((query) => String(query.id) === id);
        }
        return undefined;
      })
      .filter(Boolean) || []
  ) as CategoryQuery[];

  const advancedFilterTagIds = useMemo(() => {
    if (queriesToSearch.length === 0) return [];
    return queriesToSearch.flatMap((query) =>
      (query.tagsWatchlists || []).flatMap((tw) => tw.tags)
    );
  }, [queriesToSearch]);

  const portfoliosFilterSelected = useStableArray([
    ...(filterSelections["ticker-watchlists-filter"] || []),
    ...(filterSelections["keyword-watchlists-filter"] || []),
  ]);

  // Sync portfolio filter/highlight selections to global store for sound alerts.
  useEffect(() => {
    const advancedFilterPortfolioIds = queriesToSearch.flatMap((q) =>
      (q.portfolios || []).map((p) => p.id)
    );

    const highlightPortfolioIds = [
      ...new Set(
        [
          ...(highlightSelections["ticker-watchlists-highlight"] ?? []),
          ...(highlightSelections["keyword-watchlists-highlight"] ?? []),
        ].map((id) => Number(id))
      ),
    ];
    const filterPortfolioIds = [
      ...new Set([
        ...[
          ...(filterSelections["ticker-watchlists-filter"] ?? []),
          ...(filterSelections["keyword-watchlists-filter"] ?? []),
        ].map((id) => Number(id)),
        ...advancedFilterPortfolioIds,
      ]),
    ];

    setPortfoliosHighlight(instanceKey, highlightPortfolioIds);
    setPortfoliosFilter(instanceKey, filterPortfolioIds);

    return () => {
      setPortfoliosHighlight(instanceKey, []);
      setPortfoliosFilter(instanceKey, []);
    };
  }, [
    filterSelections,
    highlightSelections,
    queriesToSearch,
    instanceKey,
    setPortfoliosFilter,
    setPortfoliosHighlight,
  ]);

  const hasFilters =
    queriesToSearch.length > 0 ||
    basicFilterSelected.length > 0 ||
    portfoliosFilterSelected.length > 0 ||
    tagWatchlistTagIds.length > 0;

  const showSearchResults = hasFilters || searchLoading;

  // Live-only data source
  const baseHeadlines = headlines;
  const baseLoadNextPage = loadNextPage;
  const baseHasMore = hasMoreHeadlines;
  const baseIsLoading = headlinesLoading;
  const baseIsLoadingMore = headlinesLoading;

  const loadNext = showSearchResults ? loadNextSearchPage : baseLoadNextPage;
  const hasMore = showSearchResults ? hasMoreSearchResults : baseHasMore;
  const isLoadingMore = showSearchResults
    ? isLoadingMoreSearchResults
    : baseIsLoadingMore;

  const filteredHeadlines = showSearchResults ? searchResults : baseHeadlines;

  // The widget's highlighted lists in its order. Combined watchlists are
  // included: the panel offered their highlight checkbox here, and this page
  // used to ignore it.
  const highlightCandidates = useMemo(
    () =>
      buildHighlightCandidates(highlightOrder, portfolios, categoryQueries.data),
    [highlightOrder, portfolios, categoryQueries.data]
  );

  const filteredData = applyFilters(filteredHeadlines, highlightCandidates);

  // Push polling mode indicator into widget header when in polling fallback
  useEffect(() => {
    if (isPollingMode) {
      props.headerOptions?.pushAction(
        <PollingModeIndicator key="00-polling-mode" />
      );
    } else {
      props.headerOptions?.setActions((prev) =>
        prev.filter((a) => a.key !== "00-polling-mode")
      );
    }
  }, [isPollingMode, props.headerOptions]);

  /**
   * Performs a search for headlines and updates the widget's title whenever
   * the selected filters change (mirrors the v2 widget behaviour).
   */
  useEffect(() => {
    const hasAdvancedFilters = queriesToSearch.length > 0;

    const activeFilters =
      hasAdvancedFilters ||
      basicFilterSelected.length > 0 ||
      portfoliosFilterSelected.length > 0 ||
      tagWatchlistTagIds.length > 0;

    if (activeFilters) {
      const portfolioFilterIds = portfoliosFilterSelected.map((id) =>
        String(id)
      );

      const portfoliosToSearch = mapPortfolioIdsToPortfolios(
        portfolios,
        portfolioFilterIds
      );

      const allTagIds = [
        ...new Set([
          ...basicFilterSelected.map((id) => Number(id)),
          ...tagWatchlistTagIds.map((id) => Number(id)),
          ...advancedFilterTagIds.map((id) => Number(id)),
        ]),
      ];

      searchHeadlines({
        subscribed: true,
        exactMatch: false,
        rows: 50,
        portfolios: portfoliosToSearch,
        advancedFilter: queriesToSearch[0],
        tags: allTagIds,
      });

      // One flat list of what is actually applied. "Showing filtered results"
      // named the state without naming the cause, so a widget you came back to
      // could not tell you which watchlist was narrowing it.
      const titleParts: string[] = [];

      if (queriesToSearch.length > 0) {
        const filterNames = queriesToSearch
          .map((f) => f.queryName)
          .filter(Boolean)
          .map(formatListName)
          .join(", ");
        if (filterNames) titleParts.push(filterNames);
      }
      if (portfoliosToSearch.length > 0) {
        const portfolioNames = portfoliosToSearch
          .map((p) => p.name)
          .filter(Boolean)
          .map(formatListName)
          .join(", ");
        if (portfolioNames) titleParts.push(portfolioNames);
      }
      // Same gap as the current widget: tag watchlists filter the feed via
      // allTagIds but named nothing in the header.
      if (tagWatchlistsFilterSelected.length > 0) {
        const tagWatchlistNames = tagWatchlistsFilterSelected
          .map((id) => tagWatchlists.find((w) => String(w.id) === id)?.name)
          .filter((name): name is string => !!name)
          .map(formatListName)
          .join(", ");
        if (tagWatchlistNames) titleParts.push(tagWatchlistNames);
      }
      if (basicFilterSelected.length > 0) {
        const assetClassNames = resolveTagNames(basicFilterSelected);
        if (assetClassNames) titleParts.push(assetClassNames);
      }

      if (titleParts.length > 0) {
        props.headerOptions?.setTitle(
          `Filtered: ${titleParts.join(", ")}`
        );
      } else {
        props.headerOptions?.setTitle("Filtered");
      }
    } else {
      props.headerOptions?.setTitle("Newsquawk Headlines");
      clearSearchResults();
    }
  }, [
    queriesToSearch,
    basicFilterSelected,
    portfoliosFilterSelected,
    tagWatchlistTagIds,
    tagWatchlistsFilterSelected,
    tagWatchlists,
    advancedFilterTagIds,
    props.headerOptions,
    clearSearchResults,
    searchHeadlines,
    portfolios,
    filterMode,
  ]);

  // ── Track filter usage (debounced) ──────────────────────────────────
  const filterTrackingTimer = useRef<ReturnType<typeof setTimeout> | null>(
    null
  );
  useEffect(() => {
    if (filterTrackingTimer.current) clearTimeout(filterTrackingTimer.current);
    filterTrackingTimer.current = setTimeout(() => {
      if (basicFilterSelected.length > 0) {
        trackFilterApplied({
          filter_context: "headlines",
          filter_type: "category",
          filter_value:
            resolveTagNames(basicFilterSelected) ||
            basicFilterSelected.join(", "),
          filter_count: basicFilterSelected.length,
        });
      }
      if (portfoliosFilterSelected.length > 0) {
        const portfolioNames = portfoliosFilterSelected
          .map(
            (id) => portfolios?.find((p) => String(p.id) === String(id))?.name
          )
          .filter(Boolean)
          .join(", ");
        trackFilterApplied({
          filter_context: "headlines",
          filter_type: "portfolio",
          filter_value: portfolioNames || portfoliosFilterSelected.join(", "),
          filter_count: portfoliosFilterSelected.length,
        });
      }
      if (tagWatchlistTagIds.length > 0) {
        trackFilterApplied({
          filter_context: "headlines",
          filter_type: "tag_watchlist",
          filter_value: resolveTagNames(tagWatchlistTagIds) || "tag_watchlist",
          filter_count: tagWatchlistTagIds.length,
        });
      }
      if (queriesToSearch.length > 0) {
        trackFilterApplied({
          filter_context: "headlines",
          filter_type: "advanced_filter",
          filter_value: queriesToSearch.map((q) => q.queryName).join(", "),
          filter_count: queriesToSearch.length,
        });
      }
    }, 500);
    return () => {
      if (filterTrackingTimer.current)
        clearTimeout(filterTrackingTimer.current);
    };
  }, [
    basicFilterSelected,
    portfoliosFilterSelected,
    tagWatchlistTagIds,
    queriesToSearch,
    portfolios,
  ]);

  return (
    <>
      <div className="legacy-headlines h-full flex flex-col relative">
        {/* Filter Panel - inline above content */}
        <FilterHighlightPanels
        filterMenus={filterMenus}
        filterSelections={filterSelections}
        setFilterSelections={setFilterSelections}
        isFilterOpen={isFilterPanelOpen}
        onClearFilter={clearFilterSelections}
        onCloseFilter={closeFilterPanel}
        highlightMenus={highlightMenus}
        highlightSelections={highlightSelections}
        setHighlightSelections={setHighlightSelections}
        isHighlightOpen={isHighlightPanelOpen}
        onClearHighlight={clearHighlightSelections}
        onCloseHighlight={closeHighlightPanel}
        highlightOrder={highlightOrder}
        onMoveHighlight={moveHighlight}
      />

        {/* Legend bar (does not scroll away) */}
        <LegacyLegend />

        {/* Headlines list */}
        <VirtuosoWrapper
          data={filteredData}
          grouping={{
            groupBy: (items) =>
              GroupingUtils.groupByDate(
                items,
                (item) => {
                  const h = item[0];
                  return typeof h.published_at === "object"
                    ? new Date(h.published_at.unix * 1000).toISOString()
                    : (h.published_at as string);
                },
                {
                  includeYesterday: true,
                  dateFormat: {
                    weekday: "long",
                    month: "long",
                    day: "numeric",
                    year: "numeric",
                  },
                }
              ),
            // "Today" is the default state of a live feed, so labelling it spends a row
            // saying nothing — and a filtered view opening on a date header reads
            // as an archive rather than something still arriving. Later dates keep
            // their separators; only the first group is suppressed.
            hideFirstGroupHeader: true,
          }}
          scrollManager={{
            enabled: true,
            compareFirstItem: (a, b) => a?.[0]?.id === b?.[0]?.id,
          }}
          pagination={{
            hasMore,
            isLoadingMore,
            loadNext,
          }}
          renderItem={([headline, highlightInfo]) => (
            <LegacyNewsquawkHeadlineItem
              headline={headline}
              key={headline.id}
              highlightInfo={highlightInfo}
              onItemDelete={setDeletingItem}
              onItemEdit={setEditingItem}
            />
          )}
          renderGroupHeader={(title) => <div className="lh-day">{title}</div>}
          renderEmpty={() => (
            <EmptyPlaceholder
              isLoading={showSearchResults ? searchLoading : baseIsLoading}
              delay={200}
            />
          )}
          className="flex-1 scrollbar-thin bg-white dark:bg-gray-900 relative"
        />
      </div>

      {/* Dialogs */}
      <section>
        <DeleteConfirmationDialog
          isOpen={deletingItem !== null}
          onClose={() => setDeletingItem(null)}
          onConfirmDelete={
            deletingItem
              ? () => deleteMutation.mutateAsync(deletingItem.id)
              : undefined
          }
        />

        <EditHeadlineFormDialog
          isOpen={editingItem !== null}
          headline={editingItem}
          onClose={() => setEditingItem(null)}
        />
      </section>
    </>
  );
}

export default LegacyNewsquawkHeadlines;
